<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useMapGetter } from 'dashboard/composables/store';

import ReportHeader from './components/ReportHeader.vue';
import ReportFilterSelector from './components/FilterSelector.vue';
import ReportMetricCard from './components/ReportMetricCard.vue';
import SegmentedProgressBar from './components/charts/SegmentedProgressBar.vue';
import ClindCsatTable from './components/tables/ClindCsatTable.vue';
import V4Button from 'dashboard/components-next/button/Button.vue';

import { generateFileName } from '../../../../helper/downloadHelper';

const { t } = useI18n();
const store = useStore();

// Vuex getters
const metrics = useMapGetter('csat/getMetrics');
const satisfactionScore = useMapGetter('csat/getSatisfactionScore');
const problemSolvedPercentage = useMapGetter('csat/getProblemSolvedPercentage');

// Filter state
const from = ref(0);
const to = ref(0);
const userIds = ref([]);
const inboxIds = ref([]);
const team = ref(null);
const rating = ref(null);
const pageIndex = ref(0);

const requestPayload = computed(() => ({
  from: from.value,
  to: to.value,
  user_ids: userIds.value,
  inbox_ids: inboxIds.value,
  team_id: team.value,
  rating: rating.value,
  assigned_only: true,
}));

const responseCount = computed(() =>
  metrics.value.totalResponseCount
    ? metrics.value.totalResponseCount.toLocaleString()
    : '--'
);

const formattedSatisfactionScore = computed(() =>
  satisfactionScore.value ? `${satisfactionScore.value}%` : '--'
);

const formattedProblemSolved = computed(() =>
  problemSolvedPercentage.value ? `${problemSolvedPercentage.value}%` : '--'
);

// Segmented bar data for Promoters/Neutral/Detractors
const segmentedBarData = computed(() => {
  const ratingsCount = metrics.value.ratingsCount || {};
  const total = metrics.value.totalResponseCount || 0;

  if (total === 0) return [];

  const promoters = (ratingsCount[4] || 0) + (ratingsCount[5] || 0);
  const neutral = ratingsCount[3] || 0;
  const detractors = (ratingsCount[1] || 0) + (ratingsCount[2] || 0);

  return [
    {
      label: t('CSAT_REPORTS.SEGMENTS.PROMOTERS'),
      value: promoters,
      percentage: Math.round((promoters / total) * 100),
      color: '#16a34a',
    },
    {
      label: t('CSAT_REPORTS.SEGMENTS.NEUTRAL'),
      value: neutral,
      percentage: Math.round((neutral / total) * 100),
      color: '#eab308',
    },
    {
      label: t('CSAT_REPORTS.SEGMENTS.DETRACTORS'),
      value: detractors,
      percentage: Math.round((detractors / total) * 100),
      color: '#dc2626',
    },
  ];
});

// Methods
const getResponses = () => {
  store.dispatch('csat/get', {
    page: pageIndex.value + 1,
    ...requestPayload.value,
  });
};

const getAllData = () => {
  store.dispatch('csat/getMetrics', requestPayload.value);
  getResponses();
};

const downloadReports = () => {
  const type = 'csat';
  store.dispatch('csat/downloadCSATReports', {
    fileName: generateFileName({ type, to: to.value }),
    ...requestPayload.value,
  });
};

const onFilterChange = ({
  from: fromDate,
  to: toDate,
  selectedAgents,
  selectedInboxes,
  selectedTeam,
  selectedRating,
}) => {
  from.value = fromDate;
  to.value = toDate;
  userIds.value = selectedAgents?.map(el => el.id) || [];
  inboxIds.value = selectedInboxes?.map(el => el.id) || [];
  team.value = selectedTeam?.id;
  rating.value = selectedRating?.value;

  getAllData();
};

const onPageChange = newPageIndex => {
  pageIndex.value = newPageIndex;
  getResponses();
};
</script>

<template>
  <ReportHeader :header-title="$t('CSAT_REPORTS.HEADER')">
    <V4Button
      :label="$t('CSAT_REPORTS.DOWNLOAD')"
      icon="i-ph-download-simple"
      size="sm"
      @click="downloadReports"
    />
  </ReportHeader>

  <div class="flex flex-col gap-4">
    <!-- Filters -->
    <ReportFilterSelector
      show-agents-filter
      show-inbox-filter
      show-rating-filter
      :show-business-hours-switch="false"
      @filter-change="onFilterChange"
    />

    <!-- Metric Cards -->
    <div class="grid grid-cols-1 gap-4 md:grid-cols-3">
      <div class="p-4 border rounded-xl border-n-weak bg-n-solid-2">
        <ReportMetricCard
          :label="$t('CSAT_REPORTS.METRIC.SATISFACTION_SCORE.LABEL')"
          :value="formattedSatisfactionScore"
          :info-text="$t('CSAT_REPORTS.METRIC.SATISFACTION_SCORE.TOOLTIP')"
        />
      </div>
      <div class="p-4 border rounded-xl border-n-weak bg-n-solid-2">
        <ReportMetricCard
          :label="$t('CSAT_REPORTS.METRIC.TOTAL_RESPONSES.LABEL')"
          :value="responseCount"
          :info-text="$t('CSAT_REPORTS.METRIC.TOTAL_RESPONSES.TOOLTIP')"
        />
      </div>
      <div class="p-4 border rounded-xl border-n-weak bg-n-solid-2">
        <ReportMetricCard
          :label="$t('CSAT_REPORTS.METRIC.PROBLEM_SOLVED.LABEL')"
          :value="formattedProblemSolved"
          :info-text="$t('CSAT_REPORTS.METRIC.PROBLEM_SOLVED.TOOLTIP')"
        />
      </div>
    </div>

    <!-- Promoters / Neutral / Detractors -->
    <div
      v-if="metrics.totalResponseCount"
      class="p-4 border rounded-xl border-n-weak bg-n-solid-2"
    >
      <h3 class="mb-4 text-base font-medium text-n-slate-12">
        {{ $t('CSAT_REPORTS.SEGMENTS.TITLE') }}
      </h3>
      <SegmentedProgressBar
        :segments="segmentedBarData"
        height="32px"
        preserve-order
      />
    </div>

    <!-- Table -->
    <div class="p-4 border rounded-xl border-n-weak bg-n-solid-2">
      <h3 class="mb-4 text-base font-medium text-n-slate-12">
        {{ $t('CSAT_REPORTS.TABLE.TITLE') }}
      </h3>
      <ClindCsatTable :page-index="pageIndex" @page-change="onPageChange" />
    </div>
  </div>
</template>
